import React, { useState, useEffect, useCallback } from 'react';
import { getLineaCreditoById, createLineaCredito, updateLineaCredito } from '../services/lineaCreditoService';
import { getTiposCredito, getTipoCreditoById } from '../services/tipoCreditoService';
import { useNavigate } from 'react-router-dom';
import { X, RefreshCw, FileText, Calculator, DollarSign, Layers, Lock, Unlock, AlertTriangle, Save, Search, Percent } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getPlanCuentas } from '../services/planCuentaService';

export default function LineaCreditoForm({ isOpen, onClose, id: propId, onSaveSuccess }) {
    const { token } = useAuth();
    const navigate = useNavigate();
    const id = propId;
    const isEditMode = !!id;

    // Estados
    const [formData, setFormData] = useState({
        nombre: '',
        codigo: '',
        descripcion: '',
        tipoCreditoId: '',
        tasaNominal: '',
        tasaMora: '',
        plazoMinimo: '',
        plazoMaximo: '',
        montoMinimo: '',
        montoMaximo: '',
        activo: true,
        personalizarCuentas: false,
        cuentaContableCapitalOverrideId: '',
        cuentaContableInteresOverrideId: ''
    });
    const [initialData, setInitialData] = useState(null);
    const [tiposCredito, setTiposCredito] = useState([]);
    const [planCuentas, setPlanCuentas] = useState([]);
    const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingTipo, setLoadingTipo] = useState(false);
    const [esConfiguracionValida, setEsConfiguracionValida] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        if (isOpen) {
            cargarCatalogos();
            if (isEditMode) {
                cargarLinea();
            } else {
                const defaultForm = {
                    nombre: '',
                    codigo: '',
                    descripcion: '',
                    tipoCreditoId: '',
                    tasaNominal: '',
                    tasaMora: '',
                    plazoMinimo: '',
                    plazoMaximo: '',
                    montoMinimo: '',
                    montoMaximo: '',
                    activo: true,
                    personalizarCuentas: false,
                    cuentaContableCapitalOverrideId: '',
                    cuentaContableInteresOverrideId: ''
                };
                setFormData(defaultForm);
                setInitialData(defaultForm);
                setTipoSeleccionado(null);
                setEsConfiguracionValida(true);
                setHasChanges(false);
            }
        }
    }, [isOpen, id]);

    useEffect(() => {
        if (initialData) {
            const isDirty = JSON.stringify(formData) !== JSON.stringify(initialData);
            setHasChanges(isDirty);
        }
    }, [formData, initialData]);

    const handleClose = () => {
        if (hasChanges) {
            if (window.confirm('Tienes cambios sin guardar. ¿Deseas salir?')) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const cargarCatalogos = async () => {
        try {
            const [tipos, cuentas] = await Promise.all([
                getTiposCredito(token),
                getPlanCuentas(token)
            ]);
            setTiposCredito(tipos);
            setPlanCuentas(cuentas);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error al cargar catálogos');
        }
    };

    const cargarLinea = async () => {
        try {
            setLoading(true);
            const data = await getLineaCreditoById(token, id);
            const form = {
                nombre: data.nombre,
                codigo: data.codigo || '',
                descripcion: data.descripcion || '',
                tipoCreditoId: data.tipoCreditoId || '',
                tasaNominal: data.tasaNominal || '',
                tasaMora: data.tasaMora || '',
                plazoMinimo: data.plazoMinimo || '',
                plazoMaximo: data.plazoMaximo || '',
                montoMinimo: data.montoMinimo || '',
                montoMaximo: data.montoMaximo || '',
                activo: data.activo !== undefined ? data.activo : true,
                personalizarCuentas: data.personalizarCuentas || false,
                cuentaContableCapitalOverrideId: data.cuentaContableCapitalOverrideId || '',
                cuentaContableInteresOverrideId: data.cuentaContableInteresOverrideId || ''
            };
            setFormData(form);
            setInitialData(form);
            if (data.tipoCreditoId) {
                obtenerDetalleTipo(data.tipoCreditoId);
            }
        } catch (error) {
            toast.error('Error al cargar datos');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const obtenerDetalleTipo = async (tipoId) => {
        if (!tipoId) {
            setTipoSeleccionado(null);
            setEsConfiguracionValida(true);
            return;
        }
        try {
            setLoadingTipo(true);
            const data = await getTipoCreditoById(token, tipoId);
            setTipoSeleccionado(data);

            const tieneCuentas = data.cuentaContableCapitalId && data.cuentaContableInteresId;
            setEsConfiguracionValida(!!tieneCuentas);

            if (!tieneCuentas) {
                toast.error('Configuración Contable Incompleta', { icon: '🚫' });
            }

            if (!isEditMode && !formData.tasaNominal && data.tasaReferencia) {
                setFormData(prev => ({ ...prev, tasaNominal: data.tasaReferencia }));
            }
        } catch (error) {
            console.error('Error detalle tipo:', error);
        } finally {
            setLoadingTipo(false);
        }
    };

    const handleTipoChange = (e) => {
        const val = e.target.value;
        setFormData({ ...formData, tipoCreditoId: val });
        obtenerDetalleTipo(val);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!esConfiguracionValida) {
            toast.error('Configuración contable incompleta');
            return;
        }
        if (!formData.nombre.trim()) return toast.error('El nombre es requerido');
        if (!formData.tipoCreditoId) return toast.error('Debe seleccionar un tipo');

        try {
            setLoading(true);
            if (isEditMode) {
                await updateLineaCredito(token, id, formData);
                toast.success('Producto actualizado');
            } else {
                await createLineaCredito(token, formData);
                toast.success('Producto creado');
            }
            if (onSaveSuccess) onSaveSuccess();
            onClose();
        } catch (error) {
            toast.error('Error al procesar');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-[2px] animate-in fade-in duration-200">
            <div className="bg-white shadow-xl w-full max-w-6xl overflow-hidden rounded-none flex flex-col max-h-[95vh] border border-slate-300">
                <Toaster position="top-right" />

                {/* Header Estilo Oracle APEX */}
                <div className="bg-[#f2f2f2] border-b border-slate-300 p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-slate-700 p-1.5 shadow-sm">
                            <Layers className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h2 className="text-[13px] font-bold text-slate-800 uppercase tracking-tight">
                                {isEditMode ? 'Mantenimiento: ' + formData.nombre : 'Definición de Nueva Línea de Crédito'}
                            </h2>
                            <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Product Factory Core</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {hasChanges && (
                            <span className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-[9px] font-bold uppercase animate-pulse">
                                <AlertTriangle size={10} /> Cambios sin guardar
                            </span>
                        )}
                        <button onClick={handleClose} className="p-1 hover:bg-slate-200 transition-colors text-slate-400">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 bg-[#f9f9f9]">
                    <div className="grid grid-cols-1 gap-6">

                        {/* BLOQUE A: GENERAL */}
                        <div className="bg-white border border-slate-200 shadow-sm p-5 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-slate-700"></div>
                            <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-2">
                                <FileText className="w-4 h-4 text-slate-400" />
                                <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest text-[#0066cc]">Bloque A: Definición General</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="md:col-span-2">
                                    <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase tracking-tighter">Nombre de la Línea <span className="text-red-500">*</span></label>
                                    <input
                                        type="text" required
                                        className="w-full px-3 py-2 border border-slate-300 rounded-none focus:border-blue-500 focus:ring-0 outline-none text-[13px] font-semibold text-slate-800 bg-white hover:border-slate-400 transition-colors uppercase"
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase tracking-tighter">Código Interno</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-slate-300 rounded-none focus:border-blue-500 focus:ring-0 outline-none text-[13px] font-mono bg-[#fdfdfd]"
                                        value={formData.codigo}
                                        onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                                        placeholder="AUTOGENERADO"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase tracking-tighter">Descripción del Producto</label>
                                    <textarea
                                        rows="2"
                                        className="w-full px-3 py-2 border border-slate-300 rounded-none focus:border-blue-500 focus:ring-0 outline-none text-[12px] text-slate-600 bg-white resize-none"
                                        value={formData.descripcion}
                                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    ></textarea>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase tracking-tighter">Estado Operativo</label>
                                    <div className="flex items-center gap-2 h-10">
                                        <select
                                            className="w-full px-3 py-2 border border-slate-300 rounded-none bg-white text-[12px] font-bold text-slate-700 outline-none h-full"
                                            value={formData.activo}
                                            onChange={(e) => setFormData({ ...formData, activo: e.target.value === 'true' })}
                                        >
                                            <option value="true">ACTIVO / VIGENTE</option>
                                            <option value="false">INACTIVO / SUSPENDIDO</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* BLOQUE B: CONDICIONES FINANCIERAS */}
                        <div className="bg-white border border-slate-200 shadow-sm p-5 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-emerald-600"></div>
                            <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-2">
                                <DollarSign className="w-4 h-4 text-emerald-600" />
                                <h3 className="text-[11px] font-black text-emerald-700 uppercase tracking-widest">Bloque B: Condiciones Financieras</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Tipo de Crédito Base <span className="text-red-500">*</span></label>
                                    <select
                                        required
                                        className={`w-full px-3 py-2 border rounded-none bg-white text-[13px] font-bold outline-none transition-all ${!esConfiguracionValida ? 'border-red-500 bg-red-50' : 'border-slate-300 focus:border-emerald-500'}`}
                                        value={formData.tipoCreditoId}
                                        onChange={handleTipoChange}
                                    >
                                        <option value="">-- SELECCIONE CATEGORÍA MAESTRA --</option>
                                        {tiposCredito.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Tasa Nominal (%)</label>
                                        <div className="flex items-center">
                                            <div className="bg-slate-100 border border-r-0 border-slate-300 px-2 py-2 text-slate-500"><Percent size={14} /></div>
                                            <input
                                                type="number" step="0.01"
                                                className="w-full px-3 py-2 border border-slate-300 rounded-none focus:border-emerald-500 outline-none text-[13px] font-black"
                                                value={formData.tasaNominal}
                                                onChange={(e) => setFormData({ ...formData, tasaNominal: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Tasa Mora (%)</label>
                                        <input
                                            type="number" step="0.01"
                                            className="w-full px-3 py-2 border border-slate-300 rounded-none focus:border-red-500 outline-none text-[13px] font-black text-red-600"
                                            value={formData.tasaMora}
                                            onChange={(e) => setFormData({ ...formData, tasaMora: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Monto Mínimo</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-slate-300 rounded-none focus:border-emerald-500 outline-none text-[13px] font-bold text-right bg-slate-50"
                                            value={formData.montoMinimo}
                                            onChange={(e) => setFormData({ ...formData, montoMinimo: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Monto Máximo</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-slate-300 rounded-none focus:border-emerald-500 outline-none text-[13px] font-bold text-right bg-slate-50"
                                            value={formData.montoMaximo}
                                            onChange={(e) => setFormData({ ...formData, montoMaximo: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Plazo Mín (Meses)</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-slate-300 rounded-none outline-none text-[13px] font-medium"
                                            value={formData.plazoMinimo}
                                            onChange={(e) => setFormData({ ...formData, plazoMinimo: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1 uppercase">Plazo Máx (Meses)</label>
                                        <input
                                            type="number"
                                            className="w-full px-3 py-2 border border-slate-300 rounded-none outline-none text-[13px] font-medium"
                                            value={formData.plazoMaximo}
                                            onChange={(e) => setFormData({ ...formData, plazoMaximo: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* BLOQUE C: CONFIGURACIÓN CONTABLE */}
                        <div className="bg-white border border-slate-200 shadow-sm p-5 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-blue-600"></div>
                            <div className="flex items-center justify-between mb-4 border-b border-slate-100 pb-2">
                                <div className="flex items-center gap-2">
                                    <Calculator className="w-4 h-4 text-blue-600" />
                                    <h3 className="text-[11px] font-black text-blue-700 uppercase tracking-widest">Bloque C: Configuración Contable</h3>
                                </div>
                                <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 border border-blue-100">
                                    <input
                                        type="checkbox"
                                        id="personalizarCuentas"
                                        className="w-3.5 h-3.5"
                                        checked={formData.personalizarCuentas}
                                        onChange={(e) => setFormData({ ...formData, personalizarCuentas: e.target.checked })}
                                    />
                                    <label htmlFor="personalizarCuentas" className="text-[10px] font-black text-blue-800 uppercase cursor-pointer">Definir cuentas específicas para esta línea</label>
                                </div>
                            </div>

                            {!tipoSeleccionado && !formData.personalizarCuentas ? (
                                <div className="bg-slate-50 p-6 border border-dashed border-slate-200 text-center">
                                    <div className="flex flex-col items-center gap-2 opacity-40">
                                        <Search size={32} />
                                        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 italic">Seleccione un tipo base o personalice el asiento.</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* CUENTA CAPITAL */}
                                    <div className="bg-[#fcfcfc] border border-slate-200 p-4 shadow-sm">
                                        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-1">
                                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest decoration-blue-500 underline underline-offset-4">Cta. Capital (DEBE)</p>
                                            {formData.personalizarCuentas ? <Unlock size={12} className="text-blue-500" /> : <Lock size={12} className="text-slate-400" />}
                                        </div>
                                        {formData.personalizarCuentas ? (
                                            <select
                                                className="w-full px-3 py-2 border border-blue-500 bg-white text-[12px] font-bold text-slate-800 outline-none shadow-sm"
                                                value={formData.cuentaContableCapitalOverrideId}
                                                onChange={(e) => setFormData({ ...formData, cuentaContableCapitalOverrideId: e.target.value })}
                                            >
                                                <option value="">-- BUSCAR CUENTA EN EL PLAN --</option>
                                                {planCuentas.map(pc => (
                                                    <option key={pc.id} value={pc.id}>[{pc.codigoIncoop}] {pc.nombreCuenta}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <div className="opacity-80">
                                                <p className="text-[13px] font-black text-slate-800 uppercase tracking-tight">
                                                    {tipoSeleccionado?.cuentaContableCapitalNombre || 'SIN CONFIGURACIÓN'}
                                                </p>
                                                <p className="text-[11px] font-mono font-bold text-blue-600 bg-blue-50 inline-block px-1 mt-1">
                                                    {tipoSeleccionado?.cuentaContableCapitalCodigo || '---'}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* CUENTA INTERÉS */}
                                    <div className="bg-[#fcfcfc] border border-slate-200 p-4 shadow-sm">
                                        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-1">
                                            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest decoration-emerald-500 underline underline-offset-4">Cta. Interés (HABER)</p>
                                            {formData.personalizarCuentas ? <Unlock size={12} className="text-blue-500" /> : <Lock size={12} className="text-slate-400" />}
                                        </div>
                                        {formData.personalizarCuentas ? (
                                            <select
                                                className="w-full px-3 py-2 border border-blue-500 bg-white text-[12px] font-bold text-slate-800 outline-none shadow-sm"
                                                value={formData.cuentaContableInteresOverrideId}
                                                onChange={(e) => setFormData({ ...formData, cuentaContableInteresOverrideId: e.target.value })}
                                            >
                                                <option value="">-- BUSCAR CUENTA EN EL PLAN --</option>
                                                {planCuentas.map(pc => (
                                                    <option key={pc.id} value={pc.id}>[{pc.codigoIncoop}] {pc.nombreCuenta}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <div className="opacity-80">
                                                <p className="text-[13px] font-black text-slate-800 uppercase tracking-tight">
                                                    {tipoSeleccionado?.cuentaContableInteresNombre || 'SIN CONFIGURACIÓN'}
                                                </p>
                                                <p className="text-[11px] font-mono font-bold text-emerald-600 bg-emerald-50 inline-block px-1 mt-1">
                                                    {tipoSeleccionado?.cuentaContableInteresCodigo || '---'}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {!formData.personalizarCuentas && tipoSeleccionado && !esConfiguracionValida && (
                                <div className="mt-4 bg-red-50 border border-red-200 p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-red-100 text-red-600 rounded-full animate-bounce"><Lock size={16} /></div>
                                        <div>
                                            <p className="text-[11px] font-bold text-red-800 uppercase tracking-widest">Maestro Bloqueado</p>
                                            <p className="text-[10px] text-red-600 font-medium italic">El tipo "{tipoSeleccionado.nombre}" no tiene reglas de asiento. Use el override superior.</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/parametrizacion/tipos-credito/${formData.tipoCreditoId}/editar`)}
                                        className="text-[9px] font-black text-white bg-red-800 px-4 py-2 hover:bg-red-900 transition-colors uppercase border shadow-sm"
                                    >
                                        Ir al Maestro
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>
                </form>

                {/* Footer Modal Acciones Oracle Style */}
                <div className="p-4 bg-[#f2f2f2] border-t border-slate-300 flex justify-end gap-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="px-6 py-2 border border-slate-300 text-slate-600 bg-white hover:bg-slate-50 font-bold text-[11px] uppercase transition-all shadow-sm rounded-none"
                    >
                        Salir sin guardar
                    </button>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={loading || (!formData.personalizarCuentas && !esConfiguracionValida)}
                        className={`px-8 py-2 shadow-md transition-all font-black text-[11px] uppercase flex items-center gap-2 rounded-none border ${loading || (!formData.personalizarCuentas && !esConfiguracionValida)
                            ? 'bg-slate-300 text-white cursor-not-allowed border-slate-400'
                            : hasChanges
                                ? 'bg-[#0066cc] text-white hover:bg-[#004d99] border-[#0052a3] animate-pulse'
                                : 'bg-slate-600 text-white hover:bg-slate-700 border-slate-800 opacity-80'
                            }`}
                    >
                        {loading && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                        {!loading && <Save className="w-3.5 h-3.5" />}
                        {isEditMode ? 'Consolidar Cambios' : 'Registrar Nueva Línea'}
                    </button>
                </div>
            </div>
        </div>
    );
}
